import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Button, ButtonVariant } from '@/components/form/button';
import { useAuth } from '@/features/auth';

interface IProps {
  /** JSX|String component */
  children: React.ReactNode | string;
}

/**
 * This layout render as child of ProtectedLayout
 * <ProtectedLayout>
 *  <DashboardHomeLayout>....</DashboardHomeLayout>
 * </ProtectedLayout>
 *
 * Check for pages/wine/home or pages/beer/home for the implementation details.
 *
 */
export const DashboardHomeLayout: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, signout } = useAuth();
  const { setTheme }: any = useTheme();
  const { t } = useTranslation();

  const isDemoUser = user?.email.toLowerCase().includes('demo');

  const handleOnLogout = () => {
    const signedInMethod = user?.signedInMethod;
    signout();
    navigate('/?password=true');

    // when user logout, we need to keep the right method selected in the login page
    // checkout features/auth/page/login.tsx | useEffect, for more details
    if (signedInMethod) setTheme(signedInMethod);
  };

  return (
    <div className={`pt-24 md:pb-24 ${isDemoUser ? 'md:pt-36' : 'md:pt-60'}`}>
      {isDemoUser && <DemoUserAlert handleOnLogout={handleOnLogout} />}
      <h1 className="text-4xl font-bold uppercase">{t('header.dashboard')}</h1>
      <div className="flex flex-col gap-6 mt-6 md:flex-row">{children}</div>
    </div>
  );
};

const DemoUserAlert = ({ handleOnLogout }: { handleOnLogout: () => void }) => {
  return (
    <div className="p-3 mb-20 rounded-lg bg-[#fef3c7] text-sm">
      <span className="text-[#92400e] font-bold">Demo Account Alert</span>
      <p>
        For testing purposes only. Existing customers, please log in to your secure
        account. Forgot your password? Request a new one{' '}
        <Button
          variant={ButtonVariant.Link}
          classnames="underline"
          onClick={handleOnLogout}
        >
          here
        </Button>{' '}
        or contact our{' '}
        <a href="mailto:support@oculyze.net" className="underline">
          support
        </a>{' '}
        team.
      </p>
    </div>
  );
};
